import {Card, openDeleteDialog, showNotification} from 'platform/components';
import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useDeleteArticleUpdateJobItemsMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  DmsDataGrid,
  Section,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  handleApiError,
  usePermissions,
} from '@dms/shared';

import {RequiredTestIdProps, useBoolean} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {ArticleUpdateJobMaterialModal} from './components/ArticleUpdateJobMaterialModal';

interface UpdateListProps extends RequiredTestIdProps {
  isEditingDisabled: boolean;
  articleUpdateJobId: string;
}

export function UpdateList(props: UpdateListProps) {
  const [isBasketOpened, openBasket, closeBasket] = useBoolean();

  const [articleUpdateJobEdit] = usePermissions({
    permissionKeys: ['articleUpdateJobEdit'],
  });

  const [deleteItems, {isLoading: isDeletingItems}] = useDeleteArticleUpdateJobItemsMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData, deselectAll}) => {
    match(actionKey)
      .with('delete', () => {
        const ids = isArray(rowId) ? rowId : [rowId];
        openDeleteDialog({
          onConfirm: () =>
            deleteItems({
              articleUpdateJobId: props.articleUpdateJobId,
              body: {articleUpdateJobItemId: ids},
            })
              .unwrap()
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .then(deselectAll)
              .then(() => refreshData())
              .catch(handleApiError),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) =>
      mergeAll([filter, {articleUpdateJobId: props.articleUpdateJobId}]),
    [props.articleUpdateJobId]
  );

  return (
    <Section isFullHeight>
      <Card
        isFullHeight
        hasSeparator
        title={i18n.t('page.warehouse.labels.updateList')}
        actions={[
          {
            isLoading: isDeletingItems,
            isDisabled: props.isEditingDisabled || !articleUpdateJobEdit,
            type: 'button',
            variant: 'link',
            title: i18n.t('general.actions.addItems'),
            leftIcon: 'content/add_circle',
            onClick: openBasket,
            tooltip: !articleUpdateJobEdit
              ? i18n.t('module.warehouse.label.missingArticleUpdateJobEditPermission')
              : undefined,
          },
        ]}
      >
        <DmsDataGrid
          gridCode={WAREHOUSE_GRID_CODES.articleUpdateJobItems}
          actionCallback={actionCallback}
          queryModifier={queryModifier}
          data-testid={WAREHOUSE_GRID_CODES.articleUpdateJobItems}
        />
      </Card>
      <Show when={isBasketOpened}>
        <ArticleUpdateJobMaterialModal
          articleUpdateJobId={props.articleUpdateJobId}
          onClose={closeBasket}
          data-testid={testIds.warehouse.updateJobDetailUpdateList('addMaterialModal')}
        />
      </Show>
    </Section>
  );
}
